/* eslint-disable no-param-reassign */
/* eslint-disable object-curly-newline */

import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, setDataAttr, uuid, getViewportSize } from 'js/helpers.js';
import { getBreakpointMediaQuery } from 'js/constants.js';
import { scrollToAnchor } from 'js/ra.js';
import gsap from 'gsap';

const componentName = 'cmp-left-navigation';
const accordionSelector = '.cmp-accordion';
const accordionItemName = 'cmp-accordion-item';
const headerClassStr = `${accordionItemName}__header`;
const contentClassStr = `${accordionItemName}__content`;

export class LeftNavigation extends Component {
    // Set Up Elements
    elements = {
        container: this.element.querySelector(`.${componentName}__content-container`),
        imageList: this.element.querySelector(`.${componentName}__content-image-list`),
        images: this.element.querySelectorAll(`.${componentName}__content-image-frame`),
        indicators: this.element.querySelectorAll(`.${componentName}__indicator`),
        indiatorContainer: this.element.querySelector(`.${componentName}__indicator-container`),
        scroller: this.element.querySelector(`.${componentName}__scroller`),
        scrollerWrapper: this.element.querySelector(`.${componentName}__scroller-wrapper`),
        scrollSections: this.element.querySelectorAll(`.${componentName}__scroller-section`),
        scrollerContent: this.element.querySelectorAll(`.${componentName}__scroller-content`),
        // Accordion elements
        accordionItems: this.element.getElementsByClassName(accordionItemName),
        expandButton: this.element.querySelector(`${accordionSelector}__expand-all-btn`),
        collapseButton: this.element.querySelector(`${accordionSelector}__collapse-all-btn`),
        contentFrames: this.element.querySelectorAll(`.${accordionItemName}__content-frame`)
    };

    // State
    state = {
        currentIndex: -1,
        maxHeight: 0,
        maxHeightImage: 0,
        maxHeightContent: 0,
        contentHeight: [],
        componentOffset: 0,
        currentViewport: getViewportSize(),
        isScrollingAfterClick: false,
        id: this.element.id
    };

    constants = {
        subnavDesktopHeight: 50,
        subnavMobileHeight: 60,
        headerDesktopHeight: 72,
        headerMobileHeight: 56
    };

    // GSAP slideDown animation
    slideDown = (element, duration = 0.3) => {
        gsap.set(element, {
            display: 'block',
            height: 'auto',
            overflow: 'hidden'
        });

        const height = element.offsetHeight;
        gsap.set(element, { height: 0 });

        gsap.to(element, {
            height: height,
            duration: duration,
            ease: 'power2.out',
            onComplete: () => {
                gsap.set(element, {
                    height: 'auto',
                    overflow: ''
                });
            }
        });
    };

    // GSAP slideUp animation
    slideUp = (element, duration = 0.3) => {
        gsap.to(element, {
            height: 0,
            duration: duration,
            ease: 'power2.out',
            onComplete: () => {
                gsap.set(element, {
                    display: 'none',
                    height: 'auto',
                    overflow: ''
                });
            }
        });
    };

    addEventListeners = () => {
        setTimeout(() => {
            window.addEventListener('scroll', this.scrollHandler);
        }, 800);
        window.addEventListener('resize', this.resizeHandler);
        document.addEventListener('scrollend', () => {
            this.state.isScrollingAfterClick = false;
        });

        // Add event listeners to indicators
        this.elements.indicators.forEach((indicator) => {
            indicator.addEventListener('click', this.indicatorClick);
            indicator.addEventListener('keypress', (ev) => {
                if (ev.keyCode === 13) {
                    this.indicatorClick(ev);
                }
            });
        });

        // Accordion Item Header Click
        [...this.elements.accordionItems].map((ele) => {
            const headerEle = ele.querySelector(`.${headerClassStr}`);
            if (headerEle) {
                headerEle.addEventListener('click', () => {
                    const isExpanded = headerEle.getAttribute('aria-expanded') === 'true';
                    if (isExpanded) {
                        this.toggle(ele, false);
                    } else {
                        this.toggle(ele, true);
                    }
                    if (this.elements.expandButton !== null) {
                        this.toggleButtons(this.elements.expandButton, this.elements.collapseButton, [...this.elements.accordionItems]);
                    }
                });
            }
            return null;
        });

        // Add Event Listeners
        if (this.elements.expandButton !== null) {
            this.elements.expandButton.addEventListener('click', () => {
                [...this.elements.accordionItems].forEach((accordionItem) => {
                    this.toggle(accordionItem, true);
                });
                this.toggleButtons(this.elements.expandButton, this.elements.collapseButton, [...this.elements.accordionItems]);
            });
        }

        if (this.elements.collapseButton !== null) {
            this.elements.collapseButton.addEventListener('click', () => {
                [...this.elements.accordionItems].forEach((accordionItem) => {
                    this.toggle(accordionItem, false);
                });
                this.toggleButtons(this.elements.expandButton, this.elements.collapseButton, [...this.elements.accordionItems]);
            });
        }
    };

    initDom = () => {
        this.populateAccordionContent();

        this.state.currentViewport = getViewportSize();

        setTimeout(() => {
            this.setContentHeight();
            this.state.componentOffset = this.calculateComponentOffset();
            this.element.style.setProperty(
                '--top-offset',
                `${this.state.componentOffset}px`
            );
        }, 750);

        this.readUrlParams();
    };

    // Calculate top offset of component
    calculateComponentOffset = () => {
        let offset = (window.innerHeight - this.state.maxHeight) / 2;
        let subnavOffset = document.querySelector('.sub-nav') ? this.constants.subnavDesktopHeight : 0;
        // [LEGACY BREAKPOINT: width < 1280px -> getBreakpointMediaQuery('down', 'large')]
        if (subnavOffset !== 0 && getBreakpointMediaQuery('down', 'large').matches) {
            subnavOffset = this.constants.subnavMobileHeight;
        }
        // [LEGACY BREAKPOINT: width < 1024px -> getBreakpointMediaQuery('down', 'medium')]
        const headerOffset = getBreakpointMediaQuery('down', 'medium').matches ? this.constants.headerMobileHeight : this.constants.headerDesktopHeight;
        if (subnavOffset === 0) {
            offset += headerOffset;
        } else {
            offset += subnavOffset;
        }
        return offset < 130 ? 130 : offset;
    };

    // Read index from anchor tag and scroll to section
    readUrlParams = () => {
        const anchorTag = window.location.hash;
        if (anchorTag && anchorTag.includes(this.state.id) && anchorTag.includes('i=')) {
            const index = anchorTag.split('i=')[1];

            if (getViewportSize() === 'mobile') {
                if (document.readyState === 'complete') {
                    this.toggle(this.elements.accordionItems[index], true);
                    scrollToAnchor(this.elements.accordionItems[index]);
                } else {
                    window.addEventListener('load', () => {
                        this.toggle(this.elements.accordionItems[index], true);
                        scrollToAnchor(this.elements.accordionItems[index]);
                    });
                }
            } else if (document.readyState === 'complete') {
                this.scrollToSection(+index);
            } else {
                window.addEventListener('load', () => {
                    this.scrollToSection(+index);
                });
            }
        }
    };

    // Scroll to section
    scrollToSection = (index) => {
        const element = document.querySelector(`.${componentName}__scroller-section[data-id='${this.state.id}i=${index}']`);
        // Calculate offset to stick component in middle of page
        let offset = (window.innerHeight - this.state.maxHeight) / 2;
        let subnavOffset = document.querySelector('.sub-nav') ? this.constants.subnavDesktopHeight : 0;
        // [LEGACY BREAKPOINT: width < 1280px -> getBreakpointMediaQuery('down', 'large')]
        if (subnavOffset !== 0 && getBreakpointMediaQuery('down', 'large').matches) {
            subnavOffset = this.constants.subnavMobileHeight;
        }
        // [LEGACY BREAKPOINT: width < 1024px -> getBreakpointMediaQuery('down', 'medium')]
        const headerOffset = getBreakpointMediaQuery('down', 'medium').matches ? this.constants.headerMobileHeight : this.constants.headerDesktopHeight;
        if (subnavOffset === 0) {
            offset += headerOffset;
        } else {
            offset += subnavOffset;
        }
        offset = offset < 130 ? 130 : offset;
        const top = element.getBoundingClientRect().top + (window.scrollY || document.documentElement.scrollTop) - offset;
        window.scrollTo({
            top,
            behavior: 'smooth'
        });
    };

    updateActiveState = (index) => {
        if (index === this.state.currentIndex) return;
        this.state.currentIndex = index;
        this.elements.indicators.forEach((item) => {
            item.classList.remove('active');
            if (!this.state.isScrollingAfterClick) {
                item.classList.remove('clicked');
            }
        });
        this.elements.images.forEach((image) => image.classList.remove('active'));
        this.elements.contentFrames.forEach((frame) => frame.classList.remove('active'));

        if (!this.state.isScrollingAfterClick) {
            this.elements.indicators[index].classList.add('active');
        }
        this.elements.images[index].classList.add('active');
        this.elements.contentFrames[index].classList.add('active');
    };

    scrollHandler = () => {
        let activeIndex = 0;
        let sectionScrollPercentage = 0;
        if (this.element.getBoundingClientRect().bottom < 0) return;
        if (this.element.getBoundingClientRect().top > window.innerHeight) return;

        this.elements.scrollSections.forEach((section, index) => {
            const rect = section.getBoundingClientRect();
            if (rect.top <= this.state.componentOffset + 50) {
                activeIndex = index;
                sectionScrollPercentage = Math.max(Math.min(1.3 * (1 - Math.max(Math.min(((rect.height + (rect.top - this.state.componentOffset)) / rect.height), 1), 0)), 1), 0);
                sectionScrollPercentage = sectionScrollPercentage.toFixed(2);
            }
        });

        const activeIndicator = this.elements.indicators[activeIndex];
        activeIndicator.querySelector(`.${componentName}__indicator-progress-bar`).style.transform = `scaleX(${sectionScrollPercentage})`;
        this.elements.indicators.forEach((indicator, index) => {
            if (index !== activeIndex) {
                indicator.querySelector(`.${componentName}__indicator-progress-bar`).style.transform = 'scaleX(0)';
            }
        });

        this.updateActiveState(activeIndex);
    };

    // Set content height
    setContentHeight = () => {
        this.elements.images.forEach((image) => {
            this.state.maxHeightImage = (image.scrollHeight > this.state.maxHeightImage) ? image.scrollHeight : this.state.maxHeightImage;
        });
        this.elements.scrollerContent.forEach((content) => {
            this.state.maxHeightContent = (content.scrollHeight > this.state.maxHeightContent) ? content.scrollHeight : this.state.maxHeightContent;
        });
        this.state.maxHeight = (this.state.maxHeightImage > this.state.maxHeightContent) ? this.state.maxHeightImage : this.state.maxHeightContent;

        // Set uniform heights for content
        this.elements.images.forEach((image, index) => {
            this.state.contentHeight[index] = this.elements.images[index].offsetHeight > this.elements.scrollerContent[index].offsetHeight ? this.elements.images[index].offsetHeight : this.elements.scrollerContent[index].offsetHeight;
        });
        this.elements.scrollerContent.forEach((content, index) => {
            content.style.minHeight = `${this.state.contentHeight[index]}px`;
        });

        this.elements.container.style.minHeight = `${this.elements.images[this.elements.scrollerContent.length - 1].offsetHeight}px`;
        this.elements.indiatorContainer.style.minHeight = `${this.elements.images[this.elements.scrollerContent.length - 1].offsetHeight}px`;
    };

    // Remove height styles
    unsetContentHeight = () => {
        this.element.style.removeProperty('min-height');
        this.elements.imageList.style.removeProperty('min-height');
        this.elements.container.style.removeProperty('max-height');
        this.elements.indiatorContainer.style.removeProperty('max-height');

        // Set uniform heights for content
        this.elements.images.forEach((image) => {
            image.style.removeProperty('min-height');
        });

        this.elements.scrollerContent.forEach((content) => {
            content.style.removeProperty('min-height');
        });
    };

    // Resize listener
    resizeHandler = () => {
        let newHeight = 0;
        this.elements.images.forEach((image) => {
            newHeight = (image.scrollHeight > newHeight) ? image.scrollHeight : newHeight;
        });
        this.elements.scrollerContent.forEach((content) => {
            newHeight = (content.scrollHeight > newHeight) ? content.scrollHeight : newHeight;
        });
        if (newHeight !== this.state.maxHeight && getViewportSize() !== 'mobile') {
            this.state.maxHeight = newHeight;
            this.setContentHeight();
        }
        const componentOffset = this.calculateComponentOffset();
        if (this.state.componentOffset !== componentOffset) {
            this.state.componentOffset = componentOffset;
            this.element.style.setProperty(
                '--top-offset',
                `${this.state.componentOffset}px`
            );
        }
        if (this.state.currentViewport !== getViewportSize()) {
            this.state.currentViewport = getViewportSize();
            if (this.state.currentViewport === 'mobile') {
                this.unsetContentHeight();
            } else {
                this.setContentHeight();
            }
        }
    };

    // Scroll to section on indicator click
    indicatorClick = (e) => {
        this.state.isScrollingAfterClick = true;
        e.preventDefault();
        this.elements.indicators.forEach((item) => {
            item.classList.remove('active');
            item.classList.remove('clicked');
        });
        e.target.classList.add('active');
        e.target.classList.add('clicked');
        const index = e.target.getAttribute('data-index');
        if (+index === this.state.currentIndex) return;
        //  Update hash
        window.location.hash = `${this.state.id}i=${index}`;
        // Scroll to section
        this.scrollToSection(+index);
    };

    /**
     * Accordion Functions
     */

    /**
     * Set IDs on Accordion items for aria-controls
     */
    setIds = () => {
        [...this.elements.accordionItems].map((ele) => {
            const headerEle = ele.getElementsByClassName(headerClassStr)[0];
            const contentEle = ele.getElementsByClassName(contentClassStr)[0];
            const eleUuid = uuid().replace(/-/g, '');

            setDataAttr(headerEle, 'aria-controls', eleUuid);
            contentEle.id = eleUuid;
            return null;
        });
    };

    /**
     * Set to collapse all accordion elements
     */
    setCollapse = () => {
        if (!this.elements.accordionItems) return;
        [...this.elements.accordionItems].map((ele) => {
            const scopedEle = ele;
            this.toggle(ele, false);
            scopedEle.style.opacity = 1;
            return null;
        });
    };

    /**
     * Toggle Accordion Item (expand/collapse)
     * @param {DOM Element} accordionItem - Accordion Item Element
     * @param {bool} directive - true = expand accordion | false = collapse accordion
     */
    toggle = (accordionItem, directive) => {
        if (accordionItem) {
            const headerEle = accordionItem.getElementsByClassName(headerClassStr)[0];
            const contentEle = accordionItem.getElementsByClassName(contentClassStr)[0];

            if (directive) {
                // Expand Accordion
                this.slideDown(contentEle, 0);
                setDataAttr(headerEle, 'aria-expanded', 'true');
                setDataAttr(contentEle, 'aria-hidden', 'false');
                accordionItem.setAttribute('data-expanded', 'true');
            } else {
                // Collapse Accordion
                this.slideUp(contentEle, 0);
                setDataAttr(headerEle, 'aria-expanded', 'false');
                setDataAttr(contentEle, 'aria-hidden', 'true');
                accordionItem.setAttribute('data-expanded', 'false');
            }
        }
    };

    toggleButtons = (openButton, closeButton, accordionItems) => {
        closeButton.classList.remove('hide');
        openButton.classList.add('hide');
        accordionItems.forEach((item) => {
            if (item.querySelector(`.${headerClassStr}`).getAttribute('aria-expanded') === 'false') {
                closeButton.classList.add('hide');
                openButton.classList.remove('hide');
            }
        });
    };

    /**
     * Expand Accordions in Edit Mode
     */
    editModeExpand = () => {
        if (!this.elements.accordionItems) return;
        [...this.elements.accordionItems].map((ele) => {
            this.toggle(ele, true);
            return null;
        });
    };

    /**
     * Expand Initial Accordion Item (if author option set)
     */
    initialExpand = () => {
        let initialExpand = this.element.dataset.initiallyExpandedItem;
        if (initialExpand) {
            initialExpand = parseInt(initialExpand, 10) - 1;
            this.toggle(this.elements.accordionItems[initialExpand], true);
        }
    };

    /**
     * Populate Accordion Content
     */
    populateAccordionContent = () => {
        if (!this.elements.contentFrames) return;
        [...this.elements.contentFrames].forEach((frame, index) => {
            frame.innerHTML = this.elements.scrollerContent[index].innerHTML;
        });
    };

    /**
     * Initialize Component
     */
    init() {
        // Set up Data
        this.maxHeightStr = '0';

        // Fire Init Funcs
        if (!isEditModeFooter()) {
            // Production Mode
            this.setIds();
            this.setCollapse();
            this.initialExpand();
            this.initDom();
            this.addEventListeners();
        } else {
            // Edit Mode
            this.setCollapse();
            this.editModeExpand();
        }
    }

}

registerComponent(`.${componentName}`, LeftNavigation);
